/* eslint-disable @typescript-eslint/no-explicit-any */
import { AlternateLanguage } from "@prismicio/client";
import { appLanguages, appLocales, getLocalFromPrismic, getPrismicLocale, Locales } from ".";
import { routes, staticRoutes } from "@/prismicio";
import { segments, actions } from "@/locales/segment";
import { CategorieDocument, ProductDocumentDataCategoriesItem } from "@/prismicio-types";
import { getField } from "../prismic/data";
import { getAllByIDs } from "../prismic";
import { resolveSegmentKey } from "@/helpers/templates";
import { resolveAlternates } from "@/helpers/seo";

/**
 * Translate a string
 *
 * @param {string} str
 *
 * @returns {Locales}
 */
export function __(str: string, lang: Locales) {
  const dictionary: { [key: string]: string } = appLanguages[lang]?.locales;

  if (!dictionary) {
    return str;
  }

  const translated = dictionary[str];
  const isLocal = process.env.NODE_ENV === "development";

  return translated || (isLocal && lang !== appLocales.defaultNs ? "⚠️ Missing `" + lang + "`: " : "") + str;
}
/**
 * Translate a string with variables in it
 *
 * @param {string} str
 * @param {{[key: string]: string}} context
 *
 * @returns {Locales}
 */
export function _x(str: string, context: { [key: string]: string }, lang: Locales = appLocales.defaultNs) {
  const dictionary: { [key: string]: string } = appLanguages[lang].locales;

  if (!dictionary) {
    return str;
  }

  let translated = dictionary[str] || str;

  Object.keys(context).forEach((key) => {
    translated = translated.replace(`{{ ${key} }}`, context[key]);
  });

  return translated;
}

/**
 * Resolve the translated path
 *
 * @param {AlternateLanguage} alternate
 *
 * @returns {string}
 */
export function resolveTranslatedPath(alternate: AlternateLanguage) {
  const route = (routes as any)?.find((route: any) => {
    return route.type === alternate.type;
  });

  if (!route) return `/${getLocalFromPrismic(alternate.lang)}/`;

  let path = route.path;

  const params = {
    lang: getLocalFromPrismic(alternate.lang) === appLocales.defaultLocale ? "" : getLocalFromPrismic(alternate.lang),
    uid: alternate.uid,
  };

  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });

  // Get the current segment
  let segment = segments[getLocalFromPrismic(alternate.lang)]?.[alternate.type];

  if (route.type === "inspiration_category") {
    segment = segments[getLocalFromPrismic(alternate.lang)]?.inspiration || segment;
  }

  if (segment) {
    const defaultSegment = segments[appLocales.defaultLocale]?.[alternate.type];

    path = path.replace(`/${defaultSegment}/`, `/${segment}/`);
    path = path.replace(`/${alternate.type}/`, `/${segment}/`);
    path = path.replace(`/${route.type}/`, `/${segment}/`);
  }

  if (route.type === "inspiration_category") {
    path = path.replace("/categorie-inspiration/", `/${segment}/`);
  }

  if (route.type === "archive_inspiration") {
    segment = segments[getLocalFromPrismic(alternate.lang)]?.inspiration_archive || segment;

    path = path.replace("/inspiration", `/${segment}`);
  }

  if (route.type === "archive_table") {
    path = path.replace("/tables", `/${segment}`);
  }

  // Remove first / if appLocales.defaultLocale
  if (params.lang === "") {
    path = path.slice(1);
  }

  return path || "/";
}

interface StaticParams {
  archive: boolean;
  lang: Locales;
  template: string;
  actions: string[];
}

/**
 * Resolve the static path
 *
 * @param {StaticParams} params
 *
 * @returns {string}
 *
 */
export const resolveStaticPath = (params: StaticParams) => {
  const route = staticRoutes.find((r) => {
    return r.type === params.template;
  });

  if (!route) return `/${params.lang}/`;

  const values = {
    lang: params.lang === appLocales.defaultLocale ? "" : params.lang,
    uid: segments[params.lang][params.template],
    action: params.actions.map((action) => resolveSegmentKey(params.lang, action, true)).join("/"),
  };

  let path = route.path;

  Object.entries(values).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });

  // Remove first / if appLocales.defaultLocale
  if (values.lang === "") {
    path = path.slice(1);
  }

  return path || "/";
};

/**
 * Resolve the categories
 *
 * @param {any} document
 * @param {Locales} locale
 *
 * @returns {Promise<CategorieDocument[]>}
 *
 */
export const resolveCategories = async (
  document: any,
  locale: Locales,
  params: object = {}
): Promise<CategorieDocument[]> => {
  const categoriesIds = getField("categories", document)
    ?.map((category: ProductDocumentDataCategoriesItem) => {
      return "id" in category.category ? category.category?.id : false;
    })
    .filter(Boolean);

  const categories = (await getAllByIDs(categoriesIds, { ...params })) as CategorieDocument[];

  if (locale === appLocales.defaultLocale) {
    return categories || [];
  }

  // Get the localized categories
  const localizedCategories = categories
    ?.map((category) => {
      const alternate = category?.alternate_languages?.find((lang) => lang?.lang === getPrismicLocale(locale));

      if (!alternate) return false;

      return alternate.id;
    })
    .filter(Boolean);

  if (localizedCategories?.length > 0) {
    return (await getAllByIDs(localizedCategories as string[], {
      lang: getPrismicLocale(locale),
      ...params,
    })) as CategorieDocument[];
  }

  return [];
};

export function getPermalink(locale: Locales, segment: string, action?: string) {
  let link = ``;

  if (locale !== appLocales.defaultLocale) {
    link += `/${locale}`;
  }

  const segmentKey = segments[locale]?.[segment];

  if (segmentKey) {
    link += `/${segmentKey}`;
  }

  if (action) {
    const actionKey = actions[locale]?.[action];

    if (actionKey) {
      link += `/${actionKey}`;
    }
  }

  return link;
}

export async function resolveI18nCategoriesSwitcher(document: any) {
  const alternatesArray = await Promise.all(
    (document?.alternate_languages || []).map(async (alternate: AlternateLanguage) => {
      const langKey = getLocalFromPrismic(alternate.lang);
      const url = await resolveAlternates(alternate);
      return [langKey, url] as [string, string];
    })
  );

  const alternates: Record<string, string> = Object.fromEntries(alternatesArray);

  return alternates;
}

export async function resolveI18nSearchSwitcher(slugs: string[]) {
  const alternatesArray = await Promise.all(
    appLocales.locales.map(async (lang) => {
      const searchSegment = segments[lang].search;
      const prefix = lang === appLocales.defaultLocale ? "" : `/${lang}`;
      const pathSegments = slugs.length > 0 ? `/${slugs.join("/")}` : "";
      const url = `${prefix}/${searchSegment}${pathSegments}`;

      return [lang, url] as [string, string];
    })
  );

  const alternates: Record<string, string> = Object.fromEntries(alternatesArray);
  return alternates;
}

export const getLoginRedirectUrl = (locale: Locales, route: string, redirectTo: string): string => {
  if (typeof window !== "undefined") {
    const normalizedRoute = route.startsWith("/") ? route : `/${route}`;

    const knownLocales = Object.keys(appLocales.locales); // e.g. ['en', 'fr', 'nl']
    const firstSegment = normalizedRoute.split("/")[1];

    const isAlreadyLocalized = knownLocales.includes(firstSegment);

    const redirectPath = isAlreadyLocalized ? normalizedRoute : `/${locale}/${segments[locale].account}/${route}`;

    // Save redirectPath to localStorage
    localStorage.setItem("post_login_redirect", redirectPath);

    // Return only the login path (no need for ?redirect= param anymore)
    return getPermalink(locale, redirectTo);
  }

  // Fallback (SSR-safe)
  return "/";
};

export const getRedirectPath = (locale?: Locales): string => {
  if (typeof window !== "undefined") {
    const redirectPath = localStorage.getItem("post_login_redirect");

    if (redirectPath) {
      localStorage.removeItem("post_login_redirect");
      return redirectPath;
    }
  }

  // If no redirect stored, go to home page
  return locale ? `/${locale}` : "/";
};
